// Import directly from component files to avoid circular dependencies
import { useNavigate } from 'react-router-dom';
import { Payment } from "../components/Payment";
import { CustomerDetails } from "../components/CustomerDetails";
import { RelatedPayments } from "../components/RelatedPayments";
import { RiskScore, EventsLogsSection, ReceiptsSection } from "../../components";
import { PaymentActivitySection } from '../../components/domain-sections/Activity';
import { getActivityConfig } from '../../config/activity';
import { RISK_CHECKS } from '@shared/data';
import { usePanelSections, type SectionConfig } from '@shared/hooks/usePanelSections';
import { SectionStyleProvider } from '../../context/SectionStyleContext';
import { PAYMENT_PANEL_TEXT_STYLES } from '../constants/text-styles';
import type { Payment as PaymentEntity } from '@shared/types/payment.types';

const paymentActivity = getActivityConfig('payment');

export const PAYMENT_SECTIONS: SectionConfig[] = [
  {
    key: 'activity',
    component: PaymentActivitySection,
    props: {
      events: paymentActivity.events,
      showAddComment: paymentActivity.showAddComment,
      className: 'rounded-2xl mb-0',
    }
  },
  {
    key: 'payment',
    component: Payment,
    props: {
      className: 'rounded-2xl mb-0',
    }
  },
  {
    key: 'customerDetails',
    component: CustomerDetails,
    props: {
      className: 'rounded-2xl mb-0',
    }
  },
  {
    key: 'riskScore',
    component: RiskScore,
    props: {
      riskChecks: RISK_CHECKS,
      className: 'rounded-2xl mb-0',
    }
  },
  {
    key: 'relatedPayments',
    component: RelatedPayments,
    props: {
      className: 'rounded-2xl mb-0',
    }
  },
  {
    key: 'receipts',
    component: ReceiptsSection,
    props: {
      className: 'rounded-2xl mb-0',
    }
  },
  {
    key: 'eventsLogs',
    component: EventsLogsSection,
    props: {
      entityType: 'payment',
      className: 'rounded-2xl mb-0',
    }
  }
];

interface PaymentSectionsProps {
  isDetailView?: boolean;
}

const PREVIEW_HIDDEN_SECTIONS = new Set(['eventsLogs', 'receipts', 'relatedPayments']);

const SECTIONS_WITH_NAVIGATE = new Set(['relatedPayments', 'riskScore']);

export function PaymentSections({ isDetailView = false }: PaymentSectionsProps) {
  const navigate = useNavigate();

  const filtered = isDetailView
    ? PAYMENT_SECTIONS
    : PAYMENT_SECTIONS.filter(s => !PREVIEW_HIDDEN_SECTIONS.has(s.key));

  const sections: SectionConfig[] = filtered.map(section => {
    if (!SECTIONS_WITH_NAVIGATE.has(section.key)) return section;
    if (section.key === 'relatedPayments') {
      return {
        ...section,
        props: {
          ...section.props,
          onViewPayment: (payment: PaymentEntity) => navigate(`/payments/${payment.transactionRef || payment.id}`),
        },
      };
    }
    if (section.key === 'riskScore') {
      return {
        ...section,
        props: {
          ...section.props,
          onRuleClick: (ruleId: string) => navigate(`/sonar/rules/${ruleId}`),
        },
      };
    }
    return section;
  });

  const { renderSections } = usePanelSections(sections);

  return (
    <SectionStyleProvider sectionTitleClassName={PAYMENT_PANEL_TEXT_STYLES.sectionTitle}>
      <div className="flex flex-col gap-1">{renderSections(isDetailView)}</div>
    </SectionStyleProvider>
  );
}
